import { Inject, Injectable } from '@angular/core';
import moment from 'moment';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { from, merge, Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TIMESHEET_KEYS } from '../../../enums/timesheet.enum';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../../interfaces/synchronization.interface';
import { EventService } from '../../../services/event/event.service';
import { OdataTimesheetService } from '../../../services/timesheet/odata-timesheet.service';
import { hydrateSlice, persistenceError, persistSlice, sliceHydrated, slicePersisted } from '../../persistence';
import { notificationLoaded } from '../../notification/actions/notification.actions';
import {
    selectLastNotificationLoad,
    selectNotificationEntries,
} from '../../notification/selectors/notification.selector';
import { buildTimesheetOutModel } from '../utils/build-timesheet-out-model';
import { hasLegacyStoredAddresses, normalizeStoredEntryAddresses } from '../utils/normalize-entry-addresses';
import { hasStaleNotificationInfo } from '../utils/stale-notification-info';
import { withinRetention } from '../utils/timesheet-retention';
import { hasInProgressWorkEntries } from '../utils/in-progress-work-entries';
import {
    submitConfirmationAborted,
    submitConfirmationRequested,
} from '../../confirmation/actions/confirmation.actions';
import {
    allTimesheetsRemoved,
    initTimesheet,
    removeSubmittedNotificationsFromCurrentTimesheet,
    saveTimesheetCollectionRequested,
    submitTimesheetRequested,
    timesheetCollectionSaved,
    timesheetCollectionStorageSyncError,
    timesheetEdited,
    timesheetEntryDeleted,
    timesheetManualEntryAdded,
    timesheetRemoved,
    timesheetsChangedFromStorage,
    timesheetSubmitFailed,
    timesheetSubmitPersisted,
    timesheetSubmitSynced,
    timesheetSubmitted,
    updateStartOfWork,
} from '../actions/timesheet.actions';
import {
    selectDisplayMeansOfTransport,
    selectDisplayTimesheetAddressOptions,
    selectPreviousDaysTimesheetLimit,
} from '../../config/selectors/config.selector';
import { selectIsPaceOnlineUser, selectSelectedUser } from '../../user-master/selectors/user-master.selector';
import { saveAddressesRequested } from '../../address/actions/address.actions';
import { AddressState } from '../../address/address-collection.state';
import { selectAddresses, selectAddressStatus } from '../../address/selectors/address.selector';
import {
    selectCurrentStartOfWork,
    selectCurrentTimesheet,
    selectIsTimesheetFeatureEnabled,
    selectSubmittedTimesheet,
    selectTimesheetIntegration,
} from '../selectors/timesheet.selector';
import { TIMESHEET_COLLECTION_FEATURE_KEY, TimesheetState } from '../timesheet-collection.state';
import { convertTimesheetStateToTimesheetModel } from '../../../services/timesheet/timesheet-and-expenses.ngrx.service';
import { getUuidV4 } from '../../../utilities/generic-utilities';
import { isDefined } from '../../../utilities/is-defined';

/**
 * Owns the timesheet slice's storage side effects. The NgRx store is the source of truth:
 * reducers apply edits optimistically and the persist effects mirror the slice to storage
 * through the generic persistence layer, which writes the whole slice under the single
 * `timesheetSlice` concurrency-store key (replacing the former per-key writes through
 * `StorageTimesheetService`). Storage is read only to rehydrate the slice: once on
 * `initTimesheet` and again on sync-completion (`rehydrateTimesheet$`, a one-shot read, no
 * liveQuery). `timesheetSaved` reports the persistence outcome to the facade.
 */
@Injectable()
export class TimesheetEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly odataTimesheetService: OdataTimesheetService,
        private readonly eventService: EventService,
        @Inject(SynchronizationInstanceToken)
        private readonly synchronizationService: SynchronizationInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public submitTimesheet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(submitTimesheetRequested),
            // The current timesheet slice is the source for the out model; the reducer has not
            // swapped it yet at this point (that happens on `timesheetSubmitted`). The contextual
            // inputs come from their own slices rather than from UserMasterService and
            // ConfigService, so this effect injects neither.
            concatLatestFrom(() => [
                this.store.select(selectCurrentTimesheet),
                this.store.select(selectSelectedUser),
                this.store.select(selectDisplayTimesheetAddressOptions),
                this.store.select(selectDisplayMeansOfTransport),
                this.store.select(selectAddresses),
            ]),
            concatMap(
                ([
                    { correlationId },
                    current,
                    user,
                    displayTimesheetAddressOptions,
                    displayMeansOfTransport,
                    addresses,
                ]) => {
                    if (!current) {
                        return of(
                            timesheetSubmitFailed({
                                error: 'TimesheetEffects: submitTimesheet$: no current timesheet to submit',
                                correlationId,
                            }),
                        );
                    }

                    // Build the out model and the empty replacement timesheet here, off the
                    // serializable slice, so the form model never has to cross into the store.
                    const outModel = buildTimesheetOutModel(current, {
                        frontId: user?.frontId ?? '',
                        displayTimesheetAddressOptions,
                        // the same value `UserModel.getCountry()` returned, and what feeds
                        // `clientManager.userCountry`; `selectCseCountry` is a different, config-level notion
                        country: user?.country ?? '',
                        displayMeansOfTransport,
                        addresses,
                    });

                    return this.odataTimesheetService.saveTimesheetOutModel(outModel).pipe(
                        // The reducer seeds the replacement current timesheet but cannot generate its
                        // id and stay pure, so the identity is minted here.
                        map(() => timesheetSubmitted({ correlationId, newTimesheetId: getUuidV4() })),
                        catchError((error) => {
                            this.log.error(
                                `TimesheetEffects: submitTimesheet$: ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                            );
                            return of(timesheetSubmitFailed({ error: String(error), correlationId }));
                        }),
                    );
                },
            ),
        ),
    );

    /**
     * Persists both swapped timesheets after a submit. Effects observe actions after the
     * reducers ran, so the slice already holds the accepted timesheet in `submitted` and the
     * seeded empty one in `current`; persisting the slice writes both under `timesheetSlice`.
     */
    public persistSubmittedTimesheets$ = createEffect(() =>
        this.actions$.pipe(
            ofType(timesheetSubmitted),
            concatMap(({ correlationId }) => {
                return this.persistSliceAndAwait(correlationId).pipe(
                    map(() => timesheetSubmitPersisted({ correlationId })),
                    catchError((error) => {
                        this.log.error(`TimesheetEffects: persistSubmittedTimesheets$: ${error}`);
                        return of(timesheetSubmitFailed({ error: String(error), correlationId }));
                    }),
                );
            }),
        ),
    );

    public notifySubmittedTimesheet$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(timesheetSubmitPersisted),
                concatLatestFrom(() => [
                    this.store.select(selectSubmittedTimesheet),
                    this.store.select(selectAddresses),
                    this.store.select(selectSelectedUser),
                ]),
                tap(([, submitted, addresses, user]) => {
                    if (submitted) {
                        this.eventService.triggerChangesOnSubmittingTimesheet(
                            convertTimesheetStateToTimesheetModel(
                                submitted,
                                TIMESHEET_KEYS.SUBMITTED_TIMESHEET,
                                addresses,
                                user?.country ?? '',
                            ),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    public syncAfterSubmit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(timesheetSubmitPersisted),
            concatMap(({ correlationId }) => {
                this.log.debug('TimesheetEffects: syncAfterSubmit$: sendOfflineData');
                return this.synchronizationService.sendOfflineData().pipe(
                    map(() => timesheetSubmitSynced({ correlationId })),
                    // String(error) keeps error-code strings intact — the facade
                    // re-throws them verbatim for the component's error mapping.
                    catchError((error) => {
                        this.log.error(`TimesheetEffects: syncAfterSubmit$: ${error}`);
                        return of(timesheetSubmitFailed({ error: String(error), correlationId }));
                    }),
                );
            }),
        ),
    );

    /**
     * Pulls the start of work back to the earliest travel a load came across, so a technician who
     * started travelling before opening the app still gets the whole day counted.
     *
     * Only backwards, and only within the current day: a start of work already recorded for an
     * earlier day is a finished day's and not this load's to move, and travel later than what is
     * recorded says nothing — the day began when it began. An unset or unparseable start of work
     * takes the travel outright, which is the case this exists for.
     *
     * A load that came across no travel leaves the recorded start of work as it stands, and writes
     * nothing at all when there is none to leave.
     *
     * The earliest travel is what the load's reconciliation saw while appending statuses, not
     * whatever travel the collection happens to hold, so it is read off the load marker rather than
     * re-derived from the entries — see `reconcileLoadedStatuses`.
     */
    public pullBackStartOfWork$ = createEffect(() =>
        this.actions$.pipe(
            ofType(notificationLoaded),
            concatLatestFrom(() => [
                this.store.select(selectLastNotificationLoad),
                this.store.select(selectCurrentStartOfWork),
            ]),
            map(([, lastLoad, startOfWork]) => {
                const earliestTravel = lastLoad?.earliestTravel ? moment(lastLoad.earliestTravel) : moment.invalid();
                const pullBack =
                    earliestTravel.isValid() &&
                    (!startOfWork.isValid() ||
                        (startOfWork.isSame(moment(), 'day') && earliestTravel.isBefore(startOfWork)));
                return pullBack ? earliestTravel : startOfWork;
            }),
            filter((startOfWork) => startOfWork.isValid()),
            map((startOfWork) => updateStartOfWork({ startOfWork: startOfWork.toJSON(), correlationId: getUuidV4() })),
        ),
    );

    /**
     * Clears the confirmed notifications out of a stale current timesheet once a load has delivered
     * the notifications the staleness is judged against (`hasStaleNotificationInfo` states the rule).
     *
     * Runs off `notificationLoaded` rather than being decided by the notification load itself: which
     * of the timesheet's own entries survive is this slice's business, and by the time the action is
     * observed the notification slice holds the entries the decision reads.
     *
     * Only for a setup where this cleanup applies at all — a PACE Online user, or one whose timesheet
     * feature is switched off. Everyone else keeps the confirmed notifications: their timesheet is
     * the record of the work and dropping them would take entries with it.
     */
    public removeStaleNotificationInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(notificationLoaded),
            concatLatestFrom(() => [
                this.store.select(selectCurrentTimesheet),
                this.store.select(selectNotificationEntries),
                this.store.select(selectIsPaceOnlineUser),
                this.store.select(selectIsTimesheetFeatureEnabled),
            ]),
            filter(
                ([, currentTimesheet, entries, isPaceOnlineUser, isTimesheetFeatureEnabled]) =>
                    isDefined(currentTimesheet) &&
                    (isPaceOnlineUser || !isTimesheetFeatureEnabled) &&
                    hasStaleNotificationInfo(currentTimesheet, entries, new Date()),
            ),
            map(() => removeSubmittedNotificationsFromCurrentTimesheet({ correlationId: getUuidV4() })),
        ),
    );

    public persistUpdatedCurrentTimesheet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                removeSubmittedNotificationsFromCurrentTimesheet,
                updateStartOfWork,
                // Both are reduced by the timesheet slice: a submit commits the confirmation's work
                // entries, an abort takes them back again.
                submitConfirmationRequested,
                submitConfirmationAborted,
                timesheetEdited,
                timesheetManualEntryAdded,
                timesheetEntryDeleted,
                timesheetRemoved,
                allTimesheetsRemoved,
            ),
            // Effects observe actions after the reducers ran, so the selectors
            // already hold the optimistically updated current timesheet and the
            // recorded write outcome. Handing the updated slice to the save
            // effect under the same correlationId lets the caller await this
            // specific write's persistence outcome; a failed write settles it
            // with the error the reducer recorded instead.
            concatLatestFrom(() => [
                this.store.select(selectCurrentTimesheet),
                this.store.select(selectTimesheetIntegration),
            ]),
            map(([{ correlationId }, currentTimesheet, integration]) => {
                integration?.warnings.forEach((warning) => this.log.error(warning));
                if (integration?.error || !currentTimesheet) {
                    const error =
                        integration?.error ?? 'TimesheetFormIntegration: no current timesheet in the slice to persist';
                    this.log.error(`TimesheetEffects: persistUpdatedCurrentTimesheet$: ${error}`);
                    return timesheetCollectionStorageSyncError({ error, correlationId });
                }
                return saveTimesheetCollectionRequested({
                    correlationId,
                });
            }),
        ),
    );

    /**
     * Rehydrate the timesheet slice from storage. The store is the source of truth, so this
     * reads storage only at init and again on sync-completion — a one-shot read, not the former
     * hot liveQuery — so a remote cross-device concurrency change downloaded during a sync still
     * lands in the slice. Reads the single `timesheetSlice` entry through the generic layer.
     */
    public rehydrateTimesheet$ = createEffect(() =>
        merge(this.actions$.pipe(ofType(initTimesheet)), this.eventService.syncReceiveCompleted$).pipe(
            map(() => hydrateSlice({ featureKey: TIMESHEET_COLLECTION_FEATURE_KEY })),
        ),
    );

    /**
     * Bridge the generic hydrate result into the timesheet slice.
     *
     * The clock and the retention window are read here and stamped onto the action, so the reducer
     * that applies the window stays pure. A limit the config has not supplied comes through as 0,
     * which keeps everything — so hydrating before the config slice is loaded trims nothing rather
     * than trimming wrongly, and the next hydrate does it properly.
     *
     * When the window drops something — or when storage still holds work entries of an unsubmitted
     * confirmation, which the reducer prunes — the reduced collection is written straight back:
     * storage holds what the slice no longer does, and without this it would only shrink whenever the
     * technician's next edit happened to persist.
     *
     * Also normalizes a legacy full-object stored address (`entry.address`, before it became an id)
     * into the id of its corresponding address in the address slice, minting that address when the
     * slice does not already hold it (`normalizeStoredEntryAddresses`). That resolution needs the
     * address slice's collection, but only when there is a legacy address to resolve at all
     * (`hasLegacyStoredAddresses`, checked first): the overwhelming majority of hydrates have nothing
     * to normalize and must take exactly the path they did before this normalization existed, with
     * no new dependency on the address slice. Only the minority that does normalize waits for that
     * slice's `selectAddressStatus` to become `'ready'` — otherwise every stored address would look
     * unknown against an empty collection and get minted as a duplicate. The wait cannot hang app
     * init: the address reducer's `setErrorReducer` advances status to `'ready'` even on a load
     * error, for exactly this reason.
     */
    public hydrateTimesheet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === TIMESHEET_COLLECTION_FEATURE_KEY),
            concatLatestFrom(() => this.store.select(selectPreviousDaysTimesheetLimit)),
            mergeMap(([{ data }, retentionDays]) => {
                const timesheets = data as TimesheetState[] | undefined;
                if (!isDefined(timesheets)) {
                    return of(
                        persistenceError({
                            featureKey: TIMESHEET_COLLECTION_FEATURE_KEY,
                            error: `Asked to rehydrate timesheet collection with 'undefined', therefore not updating store`,
                        }),
                    );
                }

                if (!hasLegacyStoredAddresses(timesheets)) {
                    return this.settleHydratedTimesheets(timesheets, retentionDays);
                }

                return this.store.select(selectAddressStatus).pipe(
                    filter((status) => status === 'ready'),
                    take(1),
                    concatLatestFrom(() => this.store.select(selectAddresses)),
                    mergeMap(([, addresses]) => {
                        const normalized = normalizeStoredEntryAddresses(timesheets, addresses);
                        return this.settleHydratedTimesheets(
                            normalized.timesheets,
                            retentionDays,
                            normalized.changed ? normalized.addresses : undefined,
                        );
                    }),
                );
            }),
        ),
    );

    /**
     * Reconciles a hydrated (and possibly address-normalized) timesheet collection: dispatches
     * `timesheetsChangedFromStorage`, whose reducer trims to the retention window and prunes
     * in-progress work entries, and — when that trim, that prune, or the address normalization
     * changed anything — writes storage back so it follows the slice. All three share this one
     * "did anything change, and if so persist" decision rather than each adding its own dispatch
     * site.
     *
     * @param timesheets the hydrated timesheets, normalized already if there was anything to
     * @param retentionDays the configured retention window, stamped onto the reducer action
     * @param normalizedAddresses the address collection `normalizeStoredEntryAddresses` produced,
     * when it changed anything; `undefined` when there was nothing to normalize. Persisted first, via
     * the same `saveAddressesRequested` the facade's other writes use, so a minted address is not
     * left referring only to the in-memory slice
     * @returns the actions to dispatch for this hydrate
     */
    private settleHydratedTimesheets(
        timesheets: TimesheetState[],
        retentionDays: number,
        normalizedAddresses?: AddressState[],
    ): Observable<Action> {
        const now = new Date().toISOString();
        const changed = timesheetsChangedFromStorage({ timesheets, now, retentionDays });
        const trimmed = withinRetention(timesheets, now, retentionDays).length !== timesheets.length;
        const pruned = hasInProgressWorkEntries(timesheets);

        if (!trimmed && !pruned && !normalizedAddresses) {
            return of(changed);
        }

        if (trimmed) {
            this.log.debug('TimesheetEffects: hydrateTimesheet$: trimmed timesheets outside the retention window');
        }
        if (pruned) {
            this.log.debug(
                'TimesheetEffects: hydrateTimesheet$: pruned stored work entries of unsubmitted confirmations',
            );
        }

        // Dispatched after the change, so the reducer has trimmed/normalized the slice by the time
        // the save effects read it back to persist.
        return from([
            ...(normalizedAddresses
                ? [saveAddressesRequested({ addresses: normalizedAddresses, correlationId: getUuidV4() })]
                : []),
            changed,
            saveTimesheetCollectionRequested({ correlationId: getUuidV4() }),
        ]);
    }

    public saveTimesheet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(saveTimesheetCollectionRequested),
            // The reducer has already applied the payload to the slice, so persisting the slice
            // writes exactly this save. concatMap serialises overlapping writes so a slow save
            // cannot be overtaken by a later one.
            concatMap(({ correlationId }) =>
                this.persistSliceAndAwait(correlationId).pipe(
                    // Echo the persisted shape — identical to the slice the reducer holds — so
                    // the facade rehydrates it as its return value.
                    map(() => timesheetCollectionSaved({ correlationId })),
                    catchError((error) => {
                        this.log.error(`TimesheetEffects: saveTimesheet$: ${error}`);
                        return of(timesheetCollectionStorageSyncError({ error: String(error), correlationId }));
                    }),
                ),
            ),
        ),
    );

    /**
     * Dispatch a slice persist and complete once its matching outcome arrives. The outcome
     * subscription is set up before dispatching so a synchronous result cannot be missed, and
     * the (globally unique) correlation id keeps this call from settling on a concurrent write.
     *
     * @param correlationId correlates the persist request with its `slicePersisted` /
     * `persistenceError` outcome
     * @returns an observable that completes on a successful persist and errors on failure
     */
    private persistSliceAndAwait(correlationId: string): Observable<void> {
        return new Observable<void>((subscriber) => {
            const subscription = this.actions$
                .pipe(
                    ofType(slicePersisted, persistenceError),
                    filter(
                        (action) =>
                            action.featureKey === TIMESHEET_COLLECTION_FEATURE_KEY &&
                            action.correlationId === correlationId,
                    ),
                    take(1),
                )
                .subscribe((action) => {
                    if (action.type === persistenceError.type) {
                        subscriber.error(new Error(action.error));
                        return;
                    }
                    subscriber.next(undefined);
                    subscriber.complete();
                });
            this.store.dispatch(persistSlice({ featureKey: TIMESHEET_COLLECTION_FEATURE_KEY, correlationId }));
            return () => subscription.unsubscribe();
        });
    }
}
